import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import * as api from './signatureProcessAPI';
import { getGlobalAbortController } from '../../utils/abortController';
import { pdfToBase64 } from '../../utils/HelperFunctions';

const initialState = {
  signatureProcessStatus: null,
  signatureProcesses: [],
  syncSignatureProcessesStatus: null,
  currentlyDownloadingSignedPdfsById: [],
  openedSignatureProcessTaskId: null,
  useStrongSignature: true,
  selectedLanguage: '',
  selectedSignees: [],
  creatingBase64PDFStatus: null,
  step: 0
};

export const createSignatureProcess = createAsyncThunk(
  'signatureProcess/createSignatureProcess',
  async (params) => {
    const globalAbortController = getGlobalAbortController();
    const response = await api.createSignatureProcess(params, globalAbortController);
    
    if (!response?.body) {
      throw new Error('Error creating signature process');
    }

    return response;
  }
)

export const syncSignatureProcesses = createAsyncThunk(
  'signatureProcess/syncSignatureProcesses',
  async (documentId) => {
    const response = await api.syncSignatureProcesses(documentId);
    return response;
  }
)

export const downloadSignedPdf = createAsyncThunk(
  'signatureProcess/downloadSignedPdf',
  async ({documentId, signedPdfFileId}) => {

    const response = await api.downloadSignedPdf(documentId, signedPdfFileId);
    return response;
  }
)

export const downloadSignedPdfAsBase64 = createAsyncThunk(
  'signatureProcess/downloadSignedPdfAsBase64',
  async (url, { rejectWithValue }) => {
    try {
      return await pdfToBase64(url);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const signatureProcessSlice = createSlice({
  name: 'signatureProcess',
  initialState,
  reducers: {
    clearSignatureProcesses: (state) => {
      state.signatureProcesses = [];
    },
    setVariablesToInitialState: (state, action) => {
      action.payload.forEach((variable) => {
        state[variable] = initialState[variable];
      });
    },
    incrementStep: (state) => {
      state.step += 1;
    },
    decrementStep: (state) => {
      if (state.step === 0) {
        return;
      }
      state.step -= 1;
    },
    setOpenedSignatureProcessTaskId: (state, action) => {
      state.openedSignatureProcessTaskId = action.payload;
    },
    toggleUseStrongSignature: (state) => {
      state.useStrongSignature = !state.useStrongSignature;
    },
    setSelectedLanguage: (state, action) => {
      state.selectedLanguage = action.payload;
    },
    setSelectedSignees: (state, action) => {
      state.selectedSignees = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(createSignatureProcess.pending, (state) => {
        state.signatureProcessStatus = 'creating';
      })
      .addCase(createSignatureProcess.fulfilled, (state) => {
        state.signatureProcessStatus = 'success';
        state.selectedSignees = [];
      })
      .addCase(createSignatureProcess.rejected, (state) => {
        state.signatureProcessStatus = 'error';
      })
      .addCase(syncSignatureProcesses.pending, (state) => {
        state.syncSignatureProcessesStatus = 'loading';
      })
      .addCase(syncSignatureProcesses.fulfilled, (state, action) => {
        state.signatureProcesses = action.payload.body.processes;
        state.syncSignatureProcessesStatus = 'idle';
      })
      .addCase(syncSignatureProcesses.rejected, (state) => {
        state.syncSignatureProcessesStatus = 'error';
      })
      .addCase(downloadSignedPdf.pending, (state, action) => {
        const signedPdfFileId = action.meta.arg.signedPdfFileId;
        
        if (!state.currentlyDownloadingSignedPdfsById.includes(signedPdfFileId)) {
          state.currentlyDownloadingSignedPdfsById.push(signedPdfFileId);
        }

      })
      .addCase(downloadSignedPdf.fulfilled, (state, action) => {
        const signedPdfFileId = action.meta.arg.signedPdfFileId;
        
        state.currentlyDownloadingSignedPdfsById = state.currentlyDownloadingSignedPdfsById.filter(
          (id) => id !== signedPdfFileId
        );
      })
      .addCase(downloadSignedPdf.rejected, (state, action) => {
        const signedPdfFileId = action.meta.arg.signedPdfFileId;
        
        state.currentlyDownloadingSignedPdfsById = state.currentlyDownloadingSignedPdfsById.filter(
          (id) => id !== signedPdfFileId
        );
      })
      .addCase(downloadSignedPdfAsBase64.pending, (state) => {
        state.creatingBase64PDFStatus = "creating";
      })
      .addCase(downloadSignedPdfAsBase64.fulfilled, (state) => {
        state.creatingBase64PDFStatus = "idle";
      })
      .addCase(downloadSignedPdfAsBase64.rejected, (state) => {
        state.creatingBase64PDFStatus = "error";
      })
  }
});

export const { 
  clearSignatureProcesses, 
  setOpenedSignatureProcessTaskId,
  toggleUseStrongSignature,
  setSelectedLanguage,
  setSelectedSignees,
  setVariablesToInitialState,
  incrementStep,
  decrementStep
} = signatureProcessSlice.actions;

export const selectDeclinedSignees= (state) => {

  const taskId = state.signatureProcess.openedSignatureProcessTaskId;

  const signatureProcess = state.signatureProcess.signatureProcesses.find(
    (process) => process._id === taskId
  );

  if (!signatureProcess || signatureProcess.status !== 'canceled') {
    return [];
  }

  return signatureProcess.signees.filter(
    (signee) => signee.status === 'declined'
  );
};

export const selectSignatureProcesses = (state) => state.signatureProcess.signatureProcesses || [];
export const selectHasSignatureProcesses = (state) => selectSignatureProcesses(state).length > 0;

export default signatureProcessSlice.reducer;